import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { GripVertical, Plus, Trash2, Pencil } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { KanbanCampoDefinicao } from '../../services/kanbanService';
import { useToast } from '../../hooks/use-toast';
import { kanbanService } from '../../services/kanbanService';

interface KanbanFieldDefinitionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const FIELD_TYPES = [
  { value: 'texto', label: 'Texto' },
  { value: 'textarea', label: 'Área de Texto' },
  { value: 'select', label: 'Lista (Select)' },
  { value: 'numero', label: 'Número' },
  { value: 'data', label: 'Data' },
];

const SortableItem = ({
  definicao,
  onEdit,
  onDelete,
}: {
  definicao: KanbanCampoDefinicao;
  onEdit: (d: KanbanCampoDefinicao) => void;
  onDelete: (d: KanbanCampoDefinicao) => void;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: definicao.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const tipoLabel = FIELD_TYPES.find(ft => ft.value === definicao.campoTipo)?.label || definicao.campoTipo;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white dark:bg-slate-600 border border-gray-200 dark:border-slate-500 group"
    >
      <button
        {...attributes}
        {...listeners}
        className="p-1 cursor-grab active:cursor-grabbing text-gray-300 dark:text-gray-500 flex-shrink-0"
      >
        <GripVertical size={14} />
      </button>
      <div className="flex-1 min-w-0">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-200 truncate block">
          {definicao.campoLabel}
        </span>
        <span className="text-[10px] text-gray-400 dark:text-gray-500">{tipoLabel}</span>
      </div>
      <button
        onClick={() => onEdit(definicao)}
        className="p-1 rounded text-gray-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors opacity-0 group-hover:opacity-100"
      >
        <Pencil size={14} />
      </button>
      <button
        onClick={() => onDelete(definicao)}
        className="p-1 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors opacity-0 group-hover:opacity-100"
      >
        <Trash2 size={14} />
      </button>
    </div>
  );
};

export const KanbanFieldDefinitionModal = ({
  open,
  onOpenChange,
}: KanbanFieldDefinitionModalProps) => {
  const { toast } = useToast();
  const [definicoes, setDefinicoes] = useState<KanbanCampoDefinicao[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [campoLabel, setCampoLabel] = useState('');
  const [campoTipo, setCampoTipo] = useState('texto');
  const [opcoes, setOpcoes] = useState('');
  const [saving, setSaving] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    if (open) loadDefinicoes();
  }, [open]);

  useEffect(() => {
    if (!open) {
      setShowForm(false);
      setEditId(null);
      setCampoLabel('');
      setCampoTipo('texto');
      setOpcoes('');
    }
  }, [open]);

  const loadDefinicoes = async () => {
    setLoading(true);
    try {
      const res = await kanbanService.listarDefinicoes();
      setDefinicoes(res.data.data || []);
    } catch {
      setDefinicoes([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = definicoes.findIndex(d => d.id === active.id);
    const newIndex = definicoes.findIndex(d => d.id === over.id);
    const newOrder = arrayMove(definicoes, oldIndex, newIndex);
    setDefinicoes(newOrder);

    try {
      await kanbanService.reordenarDefinicoes(
        newOrder.map((d, i) => ({ id: d.id, ordem: i }))
      );
    } catch {
      loadDefinicoes();
    }
  };

  const handleSubmit = async () => {
    if (!campoLabel.trim()) return;
    setSaving(true);
    try {
      if (editId) {
        await kanbanService.atualizarDefinicao(editId, {
          campoLabel: campoLabel.trim(),
          campoTipo,
          opcoes: campoTipo === 'select' ? opcoes : undefined,
        });
      } else {
        await kanbanService.criarDefinicao({
          campoLabel: campoLabel.trim(),
          campoTipo,
          opcoes: campoTipo === 'select' ? opcoes : undefined,
        });
      }
      setShowForm(false);
      setEditId(null);
      setCampoLabel('');
      setCampoTipo('texto');
      setOpcoes('');
      toast({ title: 'Sucesso', description: editId ? 'Campo atualizado com sucesso' : 'Campo criado com sucesso' });
      loadDefinicoes();
    } catch (e: any) {
      console.error('Erro ao salvar campo:', e);
      toast({
        title: 'Erro',
        description: e.response?.data?.message || 'Erro ao salvar campo',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (d: KanbanCampoDefinicao) => {
    setEditId(d.id);
    setCampoLabel(d.campoLabel);
    setCampoTipo(d.campoTipo);
    setOpcoes(d.opcoes || '');
    setShowForm(true);
  };

  const handleDelete = async (d: KanbanCampoDefinicao) => {
    try {
      await kanbanService.excluirDefinicao(d.id);
      toast({ title: 'Sucesso', description: 'Campo excluído com sucesso' });
      loadDefinicoes();
    } catch (e: any) {
      console.error('Erro ao excluir campo:', e);
      toast({
        title: 'Erro',
        description: e.response?.data?.message || 'Erro ao excluir campo',
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px] dark:bg-slate-700 max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="dark:text-white">Gerenciar Campos da Empresa</DialogTitle>
          <DialogDescription className="dark:text-gray-300">
            Defina os campos que estarão disponíveis para preenchimento nos tickets.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 py-2">
          {loading ? (
            <div className="flex items-center justify-center py-8 text-sm text-gray-500">
              Carregando campos...
            </div>
          ) : (
            <div className="space-y-1.5">
              {definicoes.length > 0 && (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext items={definicoes.map(d => d.id)} strategy={verticalListSortingStrategy}>
                    {definicoes.map(d => (
                      <SortableItem
                        key={d.id}
                        definicao={d}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
              )}
              {definicoes.length === 0 && (
                <p className="text-xs text-gray-400 dark:text-gray-500 italic py-2">
                  Nenhum campo definido ainda.
                </p>
              )}
            </div>
          )}

          {showForm && (
            <div className="border border-purple-200 dark:border-purple-800 rounded-lg p-4 space-y-3 bg-purple-50/50 dark:bg-purple-900/10">
              <Label className="text-xs font-semibold text-purple-700 dark:text-purple-300 uppercase tracking-wider">
                {editId ? 'Editar Campo' : 'Novo Campo'}
              </Label>
              <div className="grid gap-2">
                <Label className="text-sm text-gray-600 dark:text-gray-300">Nome do Campo</Label>
                <Input
                  value={campoLabel}
                  onChange={(e) => setCampoLabel(e.target.value)}
                  placeholder="Ex: Prioridade do Cliente"
                  className="dark:bg-slate-600 dark:text-white dark:border-slate-500"
                  onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
                />
              </div>
              <div className="grid gap-2">
                <Label className="text-sm text-gray-600 dark:text-gray-300">Tipo</Label>
                <Select value={campoTipo} onValueChange={setCampoTipo}>
                  <SelectTrigger className="dark:bg-slate-600 dark:text-white dark:border-slate-500">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FIELD_TYPES.map(ft => (
                      <SelectItem key={ft.value} value={ft.value}>{ft.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {campoTipo === 'select' && (
                <div className="grid gap-2">
                  <Label className="text-sm text-gray-600 dark:text-gray-300">Opções (uma por linha)</Label>
                  <Textarea
                    value={opcoes}
                    onChange={(e) => setOpcoes(e.target.value)}
                    placeholder="Opção 1&#10;Opção 2&#10;Opção 3"
                    className="h-20 text-sm dark:bg-slate-600 dark:text-white dark:border-slate-500 resize-none"
                  />
                </div>
              )}
              <div className="flex gap-2 pt-1">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => { setShowForm(false); setEditId(null); setCampoLabel(''); setCampoTipo('texto'); setOpcoes(''); }}
                  className="dark:bg-slate-600 dark:text-white dark:border-slate-500"
                >
                  Cancelar
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={handleSubmit}
                  disabled={!campoLabel.trim() || saving}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                >
                  {saving ? 'Salvando...' : editId ? 'Atualizar' : 'Criar'}
                </Button>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="border-t border-gray-200 dark:border-slate-600 pt-3">
          {!showForm && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowForm(true)}
              className="dark:bg-slate-600 dark:text-white dark:border-slate-500 border-dashed"
            >
              <Plus size={14} className="mr-1" />
              Novo Campo
            </Button>
          )}
          <DialogClose asChild>
            <Button variant="secondary" size="sm" className="dark:bg-slate-600 dark:text-white">
              Fechar
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
